/**
 * What a guest's invitation code opens (2026-10-21; invite.tsx, apps/api/src/events.ts):
 * the event, a greeting by name, and three buttons - yes, maybe, no - with how many are
 * coming and a short note. No account, no sign-in: the code in the address is the key, and
 * the answer can be changed any time. The host is emailed each answer.
 *
 * 2026-10-23, the owner: "when people scan the card give them an option to create an account" -
 * under the answer, a short note on FoldMyCard and a button to sign up (never needed to answer).
 *
 * 2026-10-24: the server no longer has the event's details or the guest's name (they stay on the
 * host's device). The guest's own code carries their name and the event's after "#" - read here,
 * never sent anywhere - and the rest (when, where, who to ask) is printed on their card.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type RsvpAnswer, type RsvpView } from '../api.ts';
import { readInviteHash } from '../invite-hash.ts';
import { Logo } from '../logo.tsx';
import { Link } from '../router.tsx';
import { t } from '../strings.ts';

/** FoldMyCard, for a guest who liked the card: an account of their own, free. */
function JoinBox() {
  return (
    <aside className="rsvp-join" aria-labelledby="rsvp-join-title">
      <h2 id="rsvp-join-title">{t('rsvpJoinTitle')}</h2>
      <p>{t('rsvpJoinText')}</p>
      <span className="row">
        <Link href="/signup" className="button primary">{t('rsvpJoinButton')}</Link>
        <Link href="/signin" className="button">{t('rsvpJoinSignIn')}</Link>
      </span>
    </aside>
  );
}

export function RsvpPage({ token }: { token: string }) {
  const [view, setView] = useState<RsvpView | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [answer, setAnswer] = useState<RsvpAnswer | null>(null);
  const [count, setCount] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (token === 'sample') { setMissing(t('rsvpSample')); return; }
    api.rsvp(token).then((v) => {
      setView(v);
      setAnswer(v.guest.response);
      setCount(v.guest.partySize ?? 1);
      setNote(v.guest.message ?? '');
    }).catch((err) => setMissing(err instanceof ApiError && err.status === 404 ? t('rsvpGone') : (err as Error).message));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!answer) return;
    setBusy(true);
    setError(null);
    try {
      const v = await api.answerRsvp(token, { response: answer, ...(answer !== 'no' ? { partySize: count } : {}), message: note.trim() });
      setView(v);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <main className="page narrow rsvp-page">
        <Logo />
        <h1>{t('rsvpTitle')}</h1>
        <p>{missing}</p>
        <JoinBox />
      </main>
    );
  }
  if (!view) return <main className="page narrow rsvp-page"><p className="muted">{t('loading')}</p></main>;
  const { guest } = view;
  // from the code itself: the guest's name and the event's (none on codes printed before 2026-10-24)
  const said = readInviteHash(location.hash);
  return (
    <main className="page narrow rsvp-page">
      <Logo />
      <p className="rsvp-hello">{said?.name ? t('rsvpHello', { name: said.name }) : t('rsvpHelloAnon')}</p>
      <h1>{said?.title || t('rsvpTitle')}</h1>
      {view.host && <p className="muted">{t('rsvpFrom', { host: view.host })}</p>}
      <p className="muted small">{t('rsvpOnYourCard')}</p>

      {saved ? (
        <div className="rsvp-thanks" role="status">
          <h2>{guest.response === 'no' ? t('rsvpThanksNo') : t('rsvpThanksYes')}</h2>
          <p className="muted">{t('rsvpChangeLater')}</p>
          <button onClick={() => setSaved(false)}>{t('rsvpChange')}</button>
        </div>
      ) : (
        <form className="stack rsvp-form" onSubmit={submit}>
          <h2>{guest.response ? t('rsvpYourAnswer') : t('rsvpAsk')}</h2>
          <div className="rsvp-choices" role="radiogroup" aria-label={t('rsvpAsk')}>
            {(['yes', 'maybe', 'no'] as const).map((a) => (
              <button key={a} type="button" role="radio" aria-checked={answer === a} className={`rsvp-choice ${a}${answer === a ? ' on' : ''}`}
                onClick={() => setAnswer(a)}>
                {a === 'yes' ? t('rsvpYesLong') : a === 'maybe' ? t('rsvpMaybeLong') : t('rsvpNoLong')}
              </button>
            ))}
          </div>
          {answer && answer !== 'no' && (
            <label>{t('rsvpHowMany')}
              <select value={count} onChange={(e) => setCount(Number(e.target.value))}>
                {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
          <label>{t('rsvpMessage')}
            <textarea rows={2} maxLength={300} value={note} placeholder={t('rsvpMessageHint')} onChange={(e) => setNote(e.target.value)} />
          </label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary big" disabled={!answer || busy}>{t('rsvpSend')}</button>
        </form>
      )}
      <JoinBox />
      <p className="muted small rsvp-about">{t('rsvpAbout')}</p>
    </main>
  );
}
